import { NavigateFunction } from "react-router-dom";

// canGoBackInApp reports whether the previous history entry belongs to this app.
// BrowserRouter keeps its own entry index in history.state.idx; it is reset to 0
// after a full page load, so a same-origin referrer also counts as in-app history.
export function canGoBackInApp(): boolean {
  const idx = window.history.state?.idx
  if (typeof idx === 'number' && idx > 0) {
    return true
  }
  return document.referrer.startsWith(window.location.origin + '/')
}

// goBack steps one entry back in history, or replaces the current entry with
// fallbackUrl when there is nothing to go back to (e.g. a link opened in a new tab).
export function goBack(navigate: NavigateFunction, fallbackUrl = '/') {
  if (canGoBackInApp()) {
    navigate(-1)
    return
  }
  navigate(fallbackUrl, { replace: true })
}

// Paths served by the Go backend itself (app/routing.go), not by the SPA router.
const serverPathPrefixes = ['/api/', '/public/', '/i18n/', '/Manuals/', '/install', '/dbupdate', '/fixtures']

// openAppUrl opens a URL on this host and port (e.g. from a QR/NFC tag) with SPA
// navigation, so the current page stays in history. Backend-served paths get a
// full page load. HTTP(S) links use the current app's protocol, since the QR
// generator's configured protocol may differ. Other origins are rejected.
export function openAppUrl(navigate: NavigateFunction, rawUrl: string): boolean {
  let url: URL
  try {
    url = new URL(rawUrl, window.location.origin)
  } catch {
    return false
  }
  if (url.origin !== window.location.origin) {
    if (!['http:', 'https:'].includes(url.protocol) || url.host !== window.location.host) {
      return false
    }
    url.protocol = window.location.protocol
  }
  // A leading // becomes a hostname when passed to the router. Older QR
  // codes may contain it when the configured base URL ended with a slash.
  url.pathname = url.pathname.replace(/^\/+/, '/')
  if (serverPathPrefixes.some(prefix => url.pathname.startsWith(prefix))) {
    window.location.href = url.toString()
    return true
  }
  navigate(url.pathname + url.search + url.hash)
  return true
}

export function updateSearchParams(
  search: string,
  navigate: NavigateFunction,
  newParams: Record<string, string | undefined | null>,
  pathname?: string  // optional for if you want to supply path
) {
  const params = new URLSearchParams(search);

  Object.entries(newParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      params.delete(key); // remove param if value is null/undefined
    } else {
      params.set(key, value);
    }
  });

  navigate(
    {
      ...(pathname ? { pathname } : {}),
      search: params.toString(),
    },
    { replace: true }
  );
}
